import type { InsightsMetricState } from './insightsState'
import type { ReviewsMonthlyWindow } from './mapReviewsAspects'
import type { InsightsProductsBySlugsQuery, ReviewsInsightsGrowthQuery } from '../../reviews.generated'

export interface ReviewsSwitchRow {
  key: string
  label: string | null
  logoUrl: string | null
  reviewers: number
}

export type ReviewsSwitchesScope = 'timeframe' | 'lifetime'

export interface ReviewsSwitches {
  rows: ReviewsSwitchRow[]
  switchShare: number | null
  scope: ReviewsSwitchesScope
  state: Extract<InsightsMetricState, 'ready' | 'error' | 'empty'>
}

type SwitchRow = {
  period?: string | null
  source?: string | null
  switchedFromType?: string | null
  switchedFromToolTitle?: string | null
  reviewers?: number | null
  answeredCount?: number | null
  switchedCount?: number | null
  sourcedCount?: number | null
}

type SwitchProduct = InsightsProductsBySlugsQuery['listProducts'][number]

type Source = { key: string; title: string | null; reviewers: number }

type Chosen = { listed: Source[]; other: number; switchShare: number | null; scope: ReviewsSwitchesScope }

export const SWITCH_ROWS_FLOOR = 5
export const SWITCH_SHARE_FLOOR = 20
const TOP_SOURCES = 5
const OTHER = 'other'
const NONE = 'none'

const items = (growth: ReviewsInsightsGrowthQuery | null | undefined, failed: boolean) => {
  const results = failed ? [] : (growth?.manage?.insights?.results ?? [])
  return { monthly: results[9], lifetime: results[10] }
}

const ok = (item: ReturnType<typeof items>['monthly']) => item?.status === 200 && !item.error

const monthlyRows = (item: ReturnType<typeof items>['monthly']): SwitchRow[] =>
  ok(item) && item?.response?.__typename === 'ManageInsightsToolSwitchesResponse' ? item.response.rows : []

const lifetimeRows = (item: ReturnType<typeof items>['lifetime']): SwitchRow[] =>
  ok(item) && item?.response?.__typename === 'ManageInsightsToolSwitchesLifetimeResponse' ? item.response.rows : []

const monthsIn = (rows: SwitchRow[], { start, end }: ReviewsMonthlyWindow) =>
  rows.filter((row) => row.period != null && row.period >= start && row.period <= end)

const totals = (rows: SwitchRow[]) => {
  const byPeriod = new Map(rows.map((row) => [row.period ?? '', row]))
  const sum = (read: (row: SwitchRow) => number | null | undefined) =>
    [...byPeriod.values()].reduce((acc, row) => acc + (read(row) ?? 0), 0)
  return { answered: sum((row) => row.answeredCount), switched: sum((row) => row.switchedCount), sourced: sum((row) => row.sourcedCount) }
}

const sources = (rows: SwitchRow[]): Source[] => {
  const bySource = new Map<string, Source>()
  for (const row of rows) {
    const key = row.source ?? NONE
    if (key === NONE) continue
    const source = bySource.get(key) ?? { key, title: row.switchedFromToolTitle ?? null, reviewers: 0 }
    source.reviewers += row.reviewers ?? 0
    bySource.set(key, source)
  }
  return [...bySource.values()].sort((a, b) => b.reviewers - a.reviewers || a.key.localeCompare(b.key))
}

const choose = (rows: SwitchRow[], scope: ReviewsSwitchesScope): Chosen | null => {
  const { answered, switched, sourced } = totals(rows)
  if (sourced < SWITCH_ROWS_FLOOR) return null
  const named = sources(rows)
  const listed = named.filter((source) => source.key !== OTHER)
  const tail = listed.slice(TOP_SOURCES).reduce((acc, source) => acc + source.reviewers, 0)
  const other = (named.find((source) => source.key === OTHER)?.reviewers ?? 0) + tail
  return {
    listed: listed.slice(0, TOP_SOURCES),
    other,
    switchShare: answered >= SWITCH_SHARE_FLOOR ? switched / answered : null,
    scope,
  }
}

const chosen = (growth: ReviewsInsightsGrowthQuery | null | undefined, window: ReviewsMonthlyWindow, failed: boolean) => {
  const { monthly, lifetime } = items(growth, failed)
  if (!ok(monthly) || !ok(lifetime)) return 'error'
  return choose(monthsIn(monthlyRows(monthly), window), 'timeframe') ?? choose(lifetimeRows(lifetime), 'lifetime')
}

export const reviewsSwitchSlugs = (
  growth: ReviewsInsightsGrowthQuery | null | undefined,
  window: ReviewsMonthlyWindow,
  failed: boolean,
): string[] => {
  const pick = chosen(growth, window, failed)
  return pick === 'error' || pick == null ? [] : pick.listed.map((source) => source.key)
}

export const mapReviewsSwitches = (
  growth: ReviewsInsightsGrowthQuery | null | undefined,
  window: ReviewsMonthlyWindow,
  failed: boolean,
  products: SwitchProduct[],
): ReviewsSwitches => {
  const pick = chosen(growth, window, failed)
  if (pick === 'error') return { rows: [], switchShare: null, scope: 'timeframe', state: 'error' }
  if (pick == null) return { rows: [], switchShare: null, scope: 'timeframe', state: 'empty' }
  const bySlug = new Map(products.map((product) => [product.slug, product]))
  const rows: ReviewsSwitchRow[] = pick.listed.map((source) => ({
    key: source.key,
    label: source.title ?? bySlug.get(source.key)?.title ?? source.key,
    logoUrl: bySlug.get(source.key)?.logo?.url ?? null,
    reviewers: source.reviewers,
  }))
  if (pick.other > 0) rows.push({ key: OTHER, label: null, logoUrl: null, reviewers: pick.other })
  return { rows, switchShare: pick.switchShare, scope: pick.scope, state: 'ready' }
}
